import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, Button, Title, Label, Skeleton } from '@patternfly/react-core'; // Adjust imports
import InsightsActions from './InsightsActions'; // Adjust as needed
import { Widget } from '../../types/dashboards/dashboard';
import { InsightsActionsResponse } from '../../api/types';
import { faArrowLeft, faSparkles, faTimes } from '@fortawesome/pro-regular-svg-icons';
import Loader from '../util/Loader';
import { TDashboardInightsEntity } from '../../api/shared-entity/SharedEntity';
import { InsightsActions as InsightsActionsAPI } from '../../api/lambda/InsightsActions';
import {
	LambdaDashboardSummaryRequest,
	LambdaGraphSummariesRequest,
	StatSummary,
} from '../../api/types/lambda-graph-summaries-request';
import { TDashboard } from '../../api/dashbboards/Dashboards';
import { useCommonStoreContext } from '../common-store/CommonStoreProvider';
import { useApplication, useIsGranted } from '../user/ApplicationProvider';
import { getExtendedPrompt } from '../../api/zone/getExtendedPrompt';
import { LambdaGraphSummariesResponse } from '../../api/types/lambda-graph-summaries-response';
import ChatInterface from './ChatInterface';
import { Permission } from '../../enums/permission.enum';
import DiscoverAddQuestionModal from '../../helpers/helper-components/DiscoverAddQuestionModal';
import { AxiosError } from 'axios';

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const retryWithBackoff = async <T,>(
	fn: () => Promise<T>,
	maxRetries = 3,
	baseDelayMs = 1000
): Promise<T> => {
	let lastError: Error | null = null;
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error as Error;
			if (attempt < maxRetries - 1) {
				const delayMs = baseDelayMs * Math.pow(2, attempt);
				console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delayMs}ms`);
				await delay(delayMs);
			}
		}
	}
	throw lastError;
};

interface InsightsPanelProps {
	widgets: Widget[];
	insightsActions?: { entityId: number; insightsResponse: InsightsActionsResponse }[];
	setInsightsActions: React.Dispatch<
		React.SetStateAction<{ entityId: number; insightsResponse: InsightsActionsResponse }[]>
	>;
	isInsightsEvalutor: boolean;
	isInsightsOpen: boolean;
	chosenInsightWidgets: Widget[];
	setChosenInsightWidgets: React.Dispatch<React.SetStateAction<Widget[]>>;
	setIsInsightsOpen: (open: boolean) => void;
	setIsInsightsEvalutor: (isEval: boolean) => void;
	beginInsightsRequest: () => void;
	openDashboardInsightsPanel: (open: boolean) => void;
	selectedChartWidget: Widget | undefined;
	setSelectedChartWidget: React.Dispatch<React.SetStateAction<Widget | undefined>>;
	insightsEntities: TDashboardInightsEntity[];
	dashboard: TDashboard;
	setLambdaGraphHighlights: React.Dispatch<React.SetStateAction<LambdaGraphSummariesResponse[]>>;
	lambdaGraphHighlights: LambdaGraphSummariesResponse[];
	setDashboardSummaryText: React.Dispatch<React.SetStateAction<string | undefined>>;
	dashboardSummaryText: string | undefined;
}

const InsightsDashboardPanel: React.FC<InsightsPanelProps> = ({
	widgets,
	insightsActions,
	setInsightsActions,
	isInsightsEvalutor,
	isInsightsOpen,
	chosenInsightWidgets,
	setChosenInsightWidgets,
	setIsInsightsOpen,
	setIsInsightsEvalutor,
	beginInsightsRequest,
	openDashboardInsightsPanel,
	selectedChartWidget,
	setSelectedChartWidget,
	insightsEntities,
	dashboard,
	setLambdaGraphHighlights,
	lambdaGraphHighlights,
	setDashboardSummaryText,
	dashboardSummaryText,
}) => {
	const [insightsData, setInsightsData] = useState<InsightsActionsResponse>({
		insights: [],
		actions: [],
	});
	const [loadedAtLeastOneInsight, setLoadedAtLeastOneInsight] = useState<boolean>(false);
	const [loadedWidgetIds, setLoadedWidgetIds] = useState<number[]>([]);
	const [isLoadingCalculationInsights, setIsLoadingCalculationInsights] =
		useState<boolean>(false);
	const [showDashboardSummaryInsights, setShowDashboardSummaryInsights] =
		useState<boolean>(false);
	const [announcement, setAnnouncement] = useState('');
	const { currentGraphSummaryStats } = useCommonStoreContext();
	const { zoneSettings } = useApplication();
	const [prevStatsSummary, setPrevStatsSummary] = useState<StatSummary[]>([]);
	const isGranted = useIsGranted();
	const hasCreateQuestionPerm = isGranted(Permission.CreateQuestion);
	const [isAddQuestionModalOpen, setIsAddQuestionModalOpen] = useState(false);

	const buildChatContext = (): string => {
		const parts: string[] = [];

		// Only include stats for selected graph
		if (selectedChartWidget && currentGraphSummaryStats?.length > 0) {
			const graphId =
				selectedChartWidget.chart?.toString() || selectedChartWidget.report?.toString();
			const stat = currentGraphSummaryStats.find((s) => s.graphId === graphId);
			if (stat) {
				parts.push(
					`Graph Statistics for ${stat.entityName}:\nmean=${stat.mean}, total=${stat.total}, min=${stat.overallMin}, max=${stat.overallMax}`
				);
			}
		}

		// Only include highlight for selected graph
		if (selectedChartWidget && lambdaGraphHighlights?.length > 0) {
			const graphId =
				selectedChartWidget.chart?.toString() || selectedChartWidget.report?.toString();
			const highlight = lambdaGraphHighlights.find((h) => h.graphId === graphId);
			if (highlight) {
				parts.push(`Graph Highlight:\n${highlight.highlight}`);
			}
		}

		// insightsData is already for selected graph only
		if (insightsData?.insights?.length) {
			parts.push(`Generated Insights:\n- ${insightsData.insights.join('\n- ')}`);
		}

		if (insightsData?.actions?.length) {
			parts.push(`Recommended Actions:\n- ${insightsData.actions.join('\n- ')}`);
		}

		return parts.join('\n\n');
	};

	const updateInsightWidgets = (checked: boolean, widgetId: number) => {
		const widget = widgets.find((x) => x.id === widgetId);
		const exists = chosenInsightWidgets.some((x) => x.id === widgetId);

		if (checked && widget && !exists) {
			const nameFromWidget = getNameFromWidget(widget) ?? '';
			setChosenInsightWidgets((prev) => [...prev, { ...widget, name: nameFromWidget }]);
		} else if (widget) {
			setChosenInsightWidgets((prev) => prev.filter((x) => x.id !== widgetId));
		}
	};

	useEffect(() => {
		if (!insightsActions) return;

		// Step 1: Track all widgets that are fully loaded
		const newlyLoadedIds: number[] = [];

		insightsActions.forEach(({ entityId, insightsResponse }) => {
			if (insightsResponse?.actions?.length > 0 || insightsResponse?.insights?.length > 0) {
				// We assume `entityId` maps directly to widget.id
				newlyLoadedIds.push(entityId);
			}
		});

		// Step 2: Update list of loaded widgets
		if (newlyLoadedIds.length > 0) {
			setLoadedAtLeastOneInsight(true); // used for global spinner
			setLoadedWidgetIds((prev) => [...new Set([...prev, ...newlyLoadedIds])]);
		}

		// Step 3: Update insights for selected chart widget (only if data is present)
		if (selectedChartWidget) {
			let found = insightsActions.find((x) => x.entityId === selectedChartWidget.chart);

			//if chart not found check for report next
			if (!found) {
				found = insightsActions.find((x) => x.entityId === selectedChartWidget.report);
			}

			if (
				(found && found?.insightsResponse?.actions?.length > 0) ||
				(found && found?.insightsResponse?.insights?.length > 0)
			) {
				setInsightsData(found.insightsResponse);
			}
		}
	}, [insightsActions, selectedChartWidget]);

	const closeQuestionModal = (hasAddedQuestion = false) => {
		setIsAddQuestionModalOpen(false);
	};

	useEffect(() => {
		if (currentGraphSummaryStats.length === 0) {
			return;
		}

		// Start loading when stats change
		setShowDashboardSummaryInsights(false);
		setIsLoadingCalculationInsights(true);

		if (
			(isInsightsOpen && dashboardSummaryText) ||
			(prevStatsSummary.length > 0 && prevStatsSummary == currentGraphSummaryStats) ||
			dashboardSummaryText
		) {
			setShowDashboardSummaryInsights(true);
			setIsLoadingCalculationInsights(false);
			return;
		}

		const request: LambdaGraphSummariesRequest = {
			dashboardId: dashboard.id?.toString() ?? '0',
			dashboardTitle: dashboard.name,
			filters: { range: 'last_28_days' },
			graphPayloads: currentGraphSummaryStats,
			metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
		};

		setPrevStatsSummary(currentGraphSummaryStats);

		retryWithBackoff(() => InsightsActionsAPI.GetGraphSummariesInsights(request), 2, 500)
			.then((response) => {
				setLambdaGraphHighlights(response.data);

				const dashboardSummaryRequest: LambdaDashboardSummaryRequest = {
					dashboardId: dashboard.id?.toString() ?? '0',
					dashboardTitle: dashboard.name,
					filters: { range: 'last_28_days' },
					highlights: response.data,
					metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
				};

				return retryWithBackoff(
					() => InsightsActionsAPI.GetDashboardSummaryInsights(dashboardSummaryRequest),
					3,
					1000
				);
			})
			.then((response) => {
				setDashboardSummaryText(response.data.dashboardSummary);
				setShowDashboardSummaryInsights(true);
			})
			.catch((error: AxiosError) => {
				console.error('Insights fetch error after retries:', {
					message: error.message,
					code: error.code,
					status: error.response?.status,
					data: error.response?.data,
					isNetworkError: !error.response,
				});
				setShowDashboardSummaryInsights(true);
			})
			.finally(() => {
				setIsLoadingCalculationInsights(false);
			});
	}, [currentGraphSummaryStats]);

	useEffect(() => {
		if (isInsightsOpen && dashboardSummaryText) {
			setShowDashboardSummaryInsights(true);
		}
		if (isInsightsOpen && lambdaGraphHighlights && lambdaGraphHighlights.length > 0) {
			setIsLoadingCalculationInsights(false);
		}
	}, [isInsightsOpen, dashboardSummaryText, lambdaGraphHighlights]);

	useEffect(() => {
		if (!isLoadingCalculationInsights && showDashboardSummaryInsights) {
			// Clear the live region first
			setAnnouncement('');
			// Delay slightly so the screen reader detects it as a change
			setTimeout(() => {
				setAnnouncement('Dashboard insight summary and graph summaries have loaded.');
			}, 50);
		}
	}, [isLoadingCalculationInsights, showDashboardSummaryInsights]);

	const getNameFromWidget = (widget: Widget) => {
		if (widget.widget_type == 'chart') {
			const chartName =
				insightsEntities.find((x) => x.chart?.id == widget.chart)?.chart?.name ?? '';
			return chartName;
		}

		if (widget.widget_type == 'pivot_table') {
			const tableNameName =
				insightsEntities.find(
					(x) => x.report?.id == widget.report || x.table?.id == widget.report
				)?.report?.name ?? '';
			return tableNameName;
		}
	};

	const insightWidgets = widgets.filter(
		(x) => x.widget_type === 'chart' || x.widget_type == 'pivot_table'
	);

	return (
		<div>
			<div className="dv-insights">
				<div className="pull-right close-insights">
					<FontAwesomeIcon
						icon={faTimes}
						size="2x"
						onClick={() => {
							setIsInsightsOpen(false);
							openDashboardInsightsPanel(false);
							setIsInsightsEvalutor(false);
							setSelectedChartWidget(undefined);
							setInsightsActions([]);
							setLoadedWidgetIds([]);
							setInsightsData({ actions: [], insights: [] });
							setLoadedAtLeastOneInsight(false);
						}}
					/>
				</div>
				<div className="insights-header">
					<FontAwesomeIcon
						icon={faSparkles}
						size="2x"
						color="#0073c3"
					/>
					<Title
						headingLevel="h1"
						data-testid="header-insights"
					>
						AI * Insights
					</Title>
				</div>
				<br />
				{!showDashboardSummaryInsights && (
					<>
						<Skeleton />
						<span className="spacer-xxs"></span>
						<Skeleton />
						<span className="spacer-xxs"></span>
						<Skeleton />
					</>
				)}
				{showDashboardSummaryInsights && dashboardSummaryText && !isInsightsEvalutor && (
					<label>{dashboardSummaryText}</label>
				)}

				{/* Hidden live region for announcements */}
				<div
					aria-live="polite"
					style={{
						position: 'absolute',
						left: '-9999px',
						height: '1px',
						width: '1px',
						overflow: 'hidden',
					}}
				>
					{announcement}
				</div>
			</div>
			{showDashboardSummaryInsights && dashboardSummaryText && !isInsightsEvalutor && (
				<>
					<br />
					<hr />
					<br />
				</>
			)}
			{!isInsightsEvalutor && (
				<>
					<div>
						{insightWidgets.map((widget) => (
							<div
								className="checkbox-row"
								key={widget.id}
							>
								<Checkbox
									id={`widget_chart_${widget.id ?? '0'}`}
									label={getNameFromWidget(widget)}
									className="large-checkbox"
									onChange={(checked) =>
										updateInsightWidgets(checked, widget.id ?? 0)
									}
									isChecked={
										chosenInsightWidgets.some((x) => x.id == widget.id)
											? true
											: false
									}
								/>
								<div className="dv-graph-summary">
									{isLoadingCalculationInsights && (
										<>
											<span className="spacer-xxs"></span>
											<Skeleton />
											<span className="spacer-xxs"></span>
											<Skeleton />
										</>
									)}
									{!isLoadingCalculationInsights &&
										lambdaGraphHighlights.length > 0 && (
											<label>
												{
													lambdaGraphHighlights.find((x) =>
														widget.chart
															? x.graphId == widget?.chart.toString()
															: x.graphId == widget.report?.toString()
													)?.highlight
												}
											</label>
										)}
								</div>
							</div>
						))}
					</div>
					<div className="dv-insights">
						<Button
							key="generate_graph_summary"
							variant="primary"
							className="btn-graph-summary"
							onClick={() => {
								beginInsightsRequest();
							}}
							isDisabled={chosenInsightWidgets.length == 0}
						>
							AI * Insights
						</Button>
					</div>
					<hr />
					<br />
					{hasCreateQuestionPerm && (
						<div style={{ float: 'left', marginLeft: '26%' }}>
							<b className="checkbox-row">Discover Questions</b>
							<br />
							<br />

							<Button
								key="cancel"
								variant="primary"
								className="pull-right btn-add-question"
								onClick={() => {
									setIsAddQuestionModalOpen(true);
								}}
							>
								+ Add Question
							</Button>
						</div>
					)}
				</>
			)}

			{isInsightsEvalutor && (
				<div className="sidebar-menu">
					<div
						className="back-button"
						onClick={() => {
							setIsInsightsEvalutor(false);
							setChosenInsightWidgets([]);
							setSelectedChartWidget(undefined);
							setInsightsActions([]);
							setLoadedWidgetIds([]);
							setInsightsData({ actions: [], insights: [] });
							setLoadedAtLeastOneInsight(false);
							setIsLoadingCalculationInsights(false);
							setShowDashboardSummaryInsights(true);
						}}
					>
						<FontAwesomeIcon
							icon={faArrowLeft}
							className="back-icon"
							onClick={() => {
								setIsInsightsEvalutor(false);
								setChosenInsightWidgets([]);
								setSelectedChartWidget(undefined);
								setInsightsActions([]);
								setLoadedWidgetIds([]);
								setInsightsData({ actions: [], insights: [] });
								setLoadedAtLeastOneInsight(false);
								setIsLoadingCalculationInsights(false);
								setShowDashboardSummaryInsights(true);
							}}
						/>
						<span>Back</span>
					</div>
					<hr />
					<div>
						{/* <label>Overview</label> */}
						<span className="spacer-xs"></span>
						{chosenInsightWidgets.map((widget, idx) => (
							<>
								<React.Fragment key={widget.id}>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<Label
											style={{
												width: '3500px',
												cursor: 'pointer',
												padding: '10px',
											}}
											className={
												widget.id === selectedChartWidget?.id
													? 'selected-chart-widget'
													: ''
											}
											onClick={() => {
												setSelectedChartWidget(widget);
											}}
										>
											{widget.name}
										</Label>

										{/* {!loadedWidgetIds.includes(widget.id ?? 0) && ( */}
										{!loadedAtLeastOneInsight && (
											<Loader
												customClass="custom-loader-right"
												size="md"
											/>
										)}
									</div>
								</React.Fragment>
								<span className="spacer-xxs"></span>
							</>
						))}
						<br />
					</div>
					<br />
					<hr />
					<br />
					{!loadedAtLeastOneInsight && (
						<div className="text-center">
							<Loader customClass="custom-spinner-scale" /> <br />
							<label>Please wait, generating insights…</label>
						</div>
					)}

					{(insightsData?.actions?.length > 0 || insightsData?.insights?.length > 0) && (
						<>
							<InsightsActions
								insights={insightsData}
								hasBackground={false}
							/>
							<ChatInterface
								context={buildChatContext()}
								contextId={selectedChartWidget?.id}
							/>
							<hr />
							<br />
						</>
					)}
				</div>
			)}
			<DiscoverAddQuestionModal
				isOpen={isAddQuestionModalOpen}
				onClose={closeQuestionModal}
				chartId={0}
				// eslint-disable-next-line @typescript-eslint/no-empty-function
				refetchQuestions={() => {}}
				isDashboard={true}
			/>
		</div>
	);
};

export default InsightsDashboardPanel;
